import type {
  ChoiceButtonStyle,
  ConfirmationMode,
  CustomRequirementCondition,
  CustomRequirementContentTextBlock,
  CustomRequirementPromptConfigAddress,
  CustomRequirementPromptConfigCheckbox,
  CustomRequirementPromptConfigCheckboxes,
  CustomRequirementPromptConfigDropdown,
  CustomRequirementPromptConfigLongText,
  CustomRequirementPromptConfigMultipleChoice,
  CustomRequirementPromptConfigNumber,
  CustomRequirementPromptConfigShortText,
  Iso4217CurrencyCode,
} from '@onefootprint/request-types/dashboard';
import type { Control } from 'react-hook-form';

export type NumberFormatKind = 'any' | 'currency';

type BaseQuestion = {
  id: string;
  label: string;
  // The full DataIdentifier this question vaults to: `custom.<field>` for a custom field, or, for a
  // phone/email short-text question, the identity DI `id.phone_number` / `id.email`.
  dataIdentifier: string;
  required: boolean;
  condition?: CustomRequirementCondition;
};

type TextBlockQuestion = {
  id: string;
  kind: CustomRequirementContentTextBlock['kind'];
  content: string;
  condition?: CustomRequirementCondition;
};

// Feeding `CustomRequirementShortTextFormat['format]` through react-hook-form's field-array types exceeds
// TS's instantiation depth (TS2589), so we spell out the flat string union for now.
// TODO: we'll soon revamp the form to allow configuring more complex options
type ShortTextFormat =
  | 'any'
  | 'phone_number'
  | 'email'
  | 'ssn9'
  | 'us_tax_id'
  | 'zip'
  | 'date'
  | 'vehicle_identification_number'
  | 'us_state'
  | 'country'
  | 'dob';

type ShortTextQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigShortText['kind'];
  format: ShortTextFormat;
  placeholder?: string;
  preview?: string;
  // Strings at the form layer to allow either a literal value or a `{{ vault.X }}` reference.
  regex?: string;
  minLength?: string;
  maxLength?: string;
  errorMessage?: string;
  minAge?: string;
  maxAge?: string;
  disablePlusAddressing?: boolean;
  supportedCountries?: string[];
};

type LongTextQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigLongText['kind'];
  placeholder?: string;
  preview?: string;
};

type NumberQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigNumber['kind'];
  numberFormat: NumberFormatKind;
  currencyCode?: Iso4217CurrencyCode;
  min?: number;
  max?: number;
  placeholder?: string;
  preview?: number;
};

type DropdownQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigDropdown['kind'];
  options: { value: string; label: string }[];
  includeOther?: boolean;
  placeholder?: string;
  preview?: string;
};

type CheckboxesQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigCheckboxes['kind'];
  options: { value: string; label: string }[];
  preview?: string[];
};

type CheckboxQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigCheckbox['kind'];
  preview?: boolean;
};

type MultipleChoiceQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigMultipleChoice['kind'];
  options: { value: string; label: string }[];
  preview?: string;
};

type AddressQuestion = BaseQuestion & {
  kind: CustomRequirementPromptConfigAddress['kind'];
  allowPoBox: boolean;
  supportedCountries?: string[];
};

export type Question =
  | TextBlockQuestion
  | ShortTextQuestion
  | LongTextQuestion
  | NumberQuestion
  | DropdownQuestion
  | CheckboxesQuestion
  | CheckboxQuestion
  | MultipleChoiceQuestion
  | AddressQuestion;

export type ChoiceButtonFormData = {
  id: string;
  label: string;
  value: string;
  style: ChoiceButtonStyle;
};

/** No buttons means the page keeps its stock Continue. */
export type ChoiceFormData = {
  saveValue: boolean;
  dataIdentifier: string;
  buttons: ChoiceButtonFormData[];
};

export type CustomQuestionsFormData = {
  title: string;
  subtitle: string;
  questions: Question[];
  confirmationMode: ConfirmationMode;
  choice: ChoiceFormData;
};

// react-hook-form resolves `useFieldArray`/`useWatch` template-literal paths
// against the full `Question` union (9 variants), which pushes TS 7's checker
// past its instantiation-depth limit (TS2589). Option forms cast their control
// to this shallow shape so RHF infers over a small type; runtime paths are
// unchanged. Use via `asOptionsControl(control)`.
export type OptionsFormData = {
  questions: { options: { value: string; label: string }[]; includeOther?: boolean }[];
};

export const asOptionsControl = (control: Control<CustomQuestionsFormData>): Control<OptionsFormData> =>
  control as unknown as Control<OptionsFormData>;
